import '@modules/projects/host'

import { ProjectKeyService } from '@graft/nuget-gc.webportals.projects'

import { getSessionToken } from '@common/helpers'

import { getGateways } from '@modules/gateways/gateways'
import { getProjectMembers } from '@modules/projects/projects'
import type { User } from '@modules/user'
import { useUserStore } from '@modules/user/stores/user.store'

import { buildResourceGroupTree } from './mappers/buildResourceGroupTree'
import { projectKeyToGeneratedSecrets } from './mappers/projectKeyToGeneratedSecrets'
import { projectKeyToResourceGroup } from './mappers/projectKeyToResourceGroup'
import type {
  GeneratedKeySecrets,
  PersonalProjectKeyPresence,
  ProjectKeyTableRow,
  ProjectResourceGroup,
  ProjectResourceGroupTreeItem,
} from './project-keys.interface'
import { appendCurrentUserMemberName } from './utils/appendCurrentUserMemberName'
import { appendUserProjectKeyIdentities } from './utils/appendUserProjectKeyIdentities'
import { buildProjectMemberIdentities } from './utils/buildProjectMemberIdentities'
import { buildProjectMemberNameById } from './utils/buildProjectMemberNameById'
import { countGatewaysForProjectKey } from './utils/countGatewaysForProjectKey'
import { findPersonalProjectKeyResourceGroup } from './utils/findPersonalProjectKeyResourceGroup'
import { prepareProjectKeyIdentityName } from './utils/prepareProjectKeyIdentityName'
import { resolvePersonalProjectKeyName } from './utils/resolvePersonalProjectKeyName'
import { resolvePersonalProjectKeyRegenerateName } from './utils/resolvePersonalProjectKeyRegenerateName'
import { resolveProjectKeyCreatedByName } from './utils/resolveProjectKeyCreatedByName'
import { resolveProjectKeyMutationNameFromRow } from './utils/resolveProjectKeyMutationName'

const getGenericProjectKeysRequestByProjectId = new Map<string, Promise<ProjectResourceGroup[]>>()

async function fetchGenericProjectKeys(projectId: string): Promise<ProjectResourceGroup[]> {
  const token = await getSessionToken()
  const [entities, gateways, members] = await Promise.all([
    ProjectKeyService.getGenericProjectKeys(token, projectId),
    getGateways(projectId),
    getProjectMembers(projectId),
  ])
  const currentUser = useUserStore.getState().currentUser
  const memberNameById = appendCurrentUserMemberName(buildProjectMemberNameById(members), currentUser)
  const memberIdentities = appendUserProjectKeyIdentities(buildProjectMemberIdentities(members), currentUser)

  const groups = await Promise.all(entities.map((entity) => projectKeyToResourceGroup({ entity, memberIdentities })))

  return groups.map((base) => ({
    ...base,
    gatewayConnectedCount: countGatewaysForProjectKey(gateways, base.id),
    createdBy: resolveProjectKeyCreatedByName({
      createdBy: base.createdBy,
      createdByUserId: base.createdByUserId,
      memberNameById,
    }),
  }))
}

export async function getGenericProjectKeys(projectId: string): Promise<ProjectResourceGroup[]> {
  if (!projectId) {
    return []
  }

  const inFlightRequest = getGenericProjectKeysRequestByProjectId.get(projectId)
  if (inFlightRequest) {
    return inFlightRequest
  }

  const request = (async () => {
    try {
      return await fetchGenericProjectKeys(projectId)
    } catch (error) {
      console.error('Failed to get generic project keys:', error)
      return []
    } finally {
      getGenericProjectKeysRequestByProjectId.delete(projectId)
    }
  })()

  getGenericProjectKeysRequestByProjectId.set(projectId, request)
  return request
}

export async function getGenericProjectKeysTree(projectId: string): Promise<ProjectResourceGroupTreeItem[]> {
  const groups = await getGenericProjectKeys(projectId)
  return buildResourceGroupTree(groups)
}

export async function generateGenericProjectKey(
  projectId: string,
  name: string,
  parentProjectKeyId = ''
): Promise<GeneratedKeySecrets> {
  const token = await getSessionToken()
  const entity = await ProjectKeyService.generateGenericProjectKey(token, projectId, name, parentProjectKeyId)
  return projectKeyToGeneratedSecrets(entity)
}

export async function regenerateProjectKey(projectId: string, keyName: string): Promise<GeneratedKeySecrets> {
  const token = await getSessionToken()
  const entity = await ProjectKeyService.regenerateProjectKey(token, projectId, keyName)
  return projectKeyToGeneratedSecrets(entity)
}

export async function deleteProjectKey(projectId: string, keyName: string): Promise<void> {
  const token = await getSessionToken()
  await ProjectKeyService.deleteProjectKey(token, projectId, keyName)
}

export async function regenerateProjectKeyRow(
  projectId: string,
  row: ProjectKeyTableRow,
  personalKeyName: string
): Promise<GeneratedKeySecrets> {
  const keyName =
    row.type === 'personal'
      ? prepareProjectKeyIdentityName(personalKeyName) || personalKeyName.trim()
      : resolveProjectKeyMutationNameFromRow(row, personalKeyName)
  const token = await getSessionToken()
  const entity = await ProjectKeyService.regenerateProjectKey(token, projectId, keyName)

  return projectKeyToGeneratedSecrets(entity)
}

export async function deleteProjectKeyRow(
  projectId: string,
  row: ProjectKeyTableRow,
  personalKeyName: string
): Promise<void> {
  const keyName = resolveProjectKeyMutationNameFromRow(row, personalKeyName)
  await deleteProjectKey(projectId, keyName)
}

export async function generatePersonalProjectKey(projectId: string, username: string): Promise<GeneratedKeySecrets> {
  const token = await getSessionToken()
  const preparedUsername = prepareProjectKeyIdentityName(username) || username
  const entity = await ProjectKeyService.generatePersonalProjectKey(token, projectId, preparedUsername)

  return projectKeyToGeneratedSecrets(entity)
}

export async function resolvePersonalProjectKeyMutationName(
  projectId: string,
  user: User | null | undefined
): Promise<string> {
  const fallback = resolvePersonalProjectKeyName(user)

  if (!projectId || !user?.id) {
    return fallback
  }

  try {
    const groups = await getGenericProjectKeys(projectId)
    const personalKeyGroup = findPersonalProjectKeyResourceGroup(groups, user)
    const mutationName = personalKeyGroup?.name.trim()

    if (mutationName) {
      return mutationName
    }
  } catch (error) {
    console.error('Failed to resolve personal project key mutation name:', error)
  }

  return fallback
}

export async function regeneratePersonalProjectKey(
  projectId: string,
  user: User | null | undefined,
  keyName?: string
): Promise<GeneratedKeySecrets> {
  const preparedKeyName = resolvePersonalProjectKeyRegenerateName(user, keyName)
  const token = await getSessionToken()
  const entity = await ProjectKeyService.regenerateProjectKey(token, projectId, preparedKeyName)
  return projectKeyToGeneratedSecrets(entity)
}

export async function deletePersonalProjectKey(
  projectId: string,
  user: User | null | undefined,
  keyName?: string
): Promise<void> {
  const resolvedKeyName = keyName?.trim() || (await resolvePersonalProjectKeyMutationName(projectId, user))
  await deleteProjectKey(projectId, resolvedKeyName)
}

const personalProjectKeyPresenceRequestByScope = new Map<string, Promise<PersonalProjectKeyPresence>>()

export async function getPersonalProjectKeyPresence(
  projectId: string,
  user: User | null | undefined
): Promise<PersonalProjectKeyPresence> {
  if (!projectId || !user?.id) {
    return { exists: false }
  }

  const scope = `${projectId}:${user.id}`
  const inFlightRequest = personalProjectKeyPresenceRequestByScope.get(scope)
  if (inFlightRequest) {
    return inFlightRequest
  }

  const request = (async () => {
    try {
      const groups = await getGenericProjectKeys(projectId)
      const personalKeyGroup = findPersonalProjectKeyResourceGroup(groups, user)
      const mutationName = personalKeyGroup?.name.trim()

      return {
        exists: Boolean(personalKeyGroup),
        ...(mutationName ? { mutationName } : {}),
      }
    } catch (error) {
      console.error('Failed to get personal project key presence:', error)
      return { exists: false }
    } finally {
      personalProjectKeyPresenceRequestByScope.delete(scope)
    }
  })()

  personalProjectKeyPresenceRequestByScope.set(scope, request)
  return request
}

export async function getGatewayProjectKey(
  projectId: string,
  projectKeyId: string
): Promise<GeneratedKeySecrets | null> {
  if (!projectKeyId) {
    return null
  }

  try {
    const token = await getSessionToken()
    const entity = await ProjectKeyService.getGatewayKey(token, projectId, projectKeyId)
    return projectKeyToGeneratedSecrets(entity)
  } catch (error) {
    console.error('Failed to get gateway project key:', error)
    return null
  }
}
